import { WorkerRequirementMappingLevel } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type Worker } from "@src/appV2/Worker/api/types";
import { flatMap, isEmpty } from "lodash";

import { type WorkerRequirement } from "../api/types";
import { type ApplicableWorkplacesDetails } from "./getApplicableWorkplacesTextForRequirement";

const GEOGRAPHIC_LEVELS = [
  WorkerRequirementMappingLevel.STATE,
  WorkerRequirementMappingLevel.MSA,
  WorkerRequirementMappingLevel.COUNTY,
] as const;

interface GetWorkerPlaceByLevelProps {
  level: WorkerRequirementMappingLevel;
  workerAddress?: Worker["address"];
}

function getWorkerPlaceByLevel(props: GetWorkerPlaceByLevelProps): string | undefined {
  const { level, workerAddress } = props;
  let place: string | undefined;

  // FIXME: Automatically disabled during ESLint config update
  switch (level) {
    case WorkerRequirementMappingLevel.STATE: {
      place = workerAddress?.state;
      break;
    }

    case WorkerRequirementMappingLevel.MSA: {
      place = workerAddress?.metropolitanStatisticalArea;
      break;
    }

    case WorkerRequirementMappingLevel.COUNTY: {
      place = workerAddress?.region;
      break;
    }

    default: {
      return undefined;
    }
  }

  return isEmpty(place) ? undefined : place;
}

interface GetApplicableWorkplacesDetailsProps {
  workerRequirement: WorkerRequirement;
  workerAddress?: Worker["address"];

  /**
   * worker-requirements api returns different response for paginated and non-paginated requests
   * If it's NOT paginated, requiredByWorkplacesCount is provided on the requirement, so we can use it
   * If it's paginated, it provides requirementMappings, so we can use that to get the count and they can be passed here as a prop
   */
  requiredByWorkplacesCount?: number;
}

export function getApplicableWorkplacesDetails(
  props: GetApplicableWorkplacesDetailsProps
): ApplicableWorkplacesDetails | undefined {
  const {
    workerRequirement,
    workerAddress,
    requiredByWorkplacesCount: propsRequiredByWorkplacesCount,
  } = props;
  const { level, requiredByWorkplacesCount, requirementMappings } = workerRequirement;

  if (!isDefined(level)) {
    return undefined;
  }

  if (level === WorkerRequirementMappingLevel.HCF) {
    return {
      type: "WORKPLACE",

      count: requiredByWorkplacesCount ?? propsRequiredByWorkplacesCount,
    };
  }

  if (level === WorkerRequirementMappingLevel.GLOBAL) {
    return { type: "ALL" };
  }

  if (!GEOGRAPHIC_LEVELS.includes(level) || !isDefined(requirementMappings)) {
    return undefined;
  }

  const workerPlace = getWorkerPlaceByLevel({ workerAddress, level });
  if (!isDefined(workerPlace)) {
    return undefined;
  }

  const requiredPlaces = flatMap(
    requirementMappings.filter((mapping) => mapping.attributes.level === level),
    (mapping) => mapping.attributes.requiredBy
  );

  // Either find the first place that matches the worker's place or the first place in the list
  // This is for the case when the worker's place is not in the list of places (border-states is the common case)
  const matchingPlace =
    requiredPlaces.find((place) => place.toLowerCase() === workerPlace.toLowerCase()) ??
    requiredPlaces?.[0];

  return isEmpty(matchingPlace) ? undefined : { type: "PLACE", place: matchingPlace };
}
